import { Button, confirm, SimpleDropdown } from "@devhop/ui";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EditIcon,
  FolderIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { RowActions, type RowAction } from "@/shared/ui/RowActions";
import type { CategoryDTO } from "../api/client";
import {
  buildCategoryTree,
  collectExpandableIds,
  filterCategoryTree,
  type CategoryTreeNode,
} from "../lib/build-category-tree";

type CategoriesTreeProps = {
  canManage: boolean;
  canCreate: boolean;
  isLoading: boolean;
  data: CategoryDTO[];
  searchQuery: string;
  onEdit: (row: CategoryDTO) => void;
  onAddChild: (parent: CategoryDTO) => void;
  onCreateRoot: () => void;
  onDelete: (id: string) => Promise<void>;
};

function CategoryTreeRow({
  node,
  depth,
  expanded,
  onToggle,
  canManage,
  canCreate,
  onEdit,
  onAddChild,
  onDelete,
}: {
  node: CategoryTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  canManage: boolean;
  canCreate: boolean;
  onEdit: (row: CategoryDTO) => void;
  onAddChild: (parent: CategoryDTO) => void;
  onDelete: (id: string) => Promise<void>;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const Folder = hasChildren && isOpen ? FolderOpenIcon : FolderIcon;

  const actions: RowAction[] = [
    ...(canCreate
      ? [
          {
            label: "ເພີ່ມຫມວດຍ່ອຍ",
            icon: <FolderPlusIcon className="size-4" />,
            onClick: () => onAddChild(node),
          },
        ]
      : []),
    ...(canManage
      ? [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="size-4" />,
            onClick: () => onEdit(node),
          },
          {
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <TrashIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບຫມວດຫມູ່",
                description: hasChildren
                  ? "ຫມວດນີ້ມີຫມວດຍ່ອຍ — ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ?"
                  : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຫມວດຫມູ່ນີ້?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) await onDelete(node.id);
            },
          },
        ]
      : []),
  ];

  return (
    <li>
      <div
        className="group flex items-center gap-1 rounded-md border border-transparent px-2 py-1.5 hover:border-border hover:bg-muted/40"
        style={{ paddingLeft: `${depth * 1.25 + 0.5}rem` }}
      >
        <button
          type="button"
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
          aria-label={isOpen ? "ຫຍໍ້" : "ຂະຫຍາຍ"}
          disabled={!hasChildren}
          onClick={() => hasChildren && onToggle(node.id)}
        >
          {hasChildren ? (
            isOpen ? (
              <ChevronDownIcon className="size-4" />
            ) : (
              <ChevronRightIcon className="size-4" />
            )
          ) : (
            <span className="size-4" />
          )}
        </button>

        <Folder
          className="size-4 shrink-0 text-muted-foreground"
          aria-hidden
        />

        <div className="min-w-0 flex-1">
          <span className="block truncate font-medium text-sm">{node.name}</span>
          {hasChildren ? (
            <span className="text-muted-foreground text-xs">
              {node.children.length} ຫມວດຍ່ອຍ
            </span>
          ) : null}
        </div>

        {actions.length > 0 ? (
          <div className="shrink-0">
            {/* tablet / mobile: ปุ่ม ... dropdown */}
            <div className="lg:hidden">
              <SimpleDropdown
                align="end"
                items={actions.map((a) => ({
                  label: a.label,
                  icon: a.icon,
                  variant:
                    a.variant === "destructive" ? "destructive" : undefined,
                  action: a.onClick,
                }))}
              />
            </div>
            {/* desktop: ปุ่ม inline เมื่อ hover */}
            <div className="hidden opacity-0 transition-opacity lg:block lg:group-hover:opacity-100">
              <RowActions maxInline={3} actions={actions} />
            </div>
          </div>
        ) : null}
      </div>

      {hasChildren && isOpen ? (
        <ul className="m-0 list-none p-0">
          {node.children.map((child) => (
            <CategoryTreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              canManage={canManage}
              canCreate={canCreate}
              onEdit={onEdit}
              onAddChild={onAddChild}
              onDelete={onDelete}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function CategoriesTree({
  canManage,
  canCreate,
  isLoading,
  data,
  searchQuery,
  onEdit,
  onAddChild,
  onCreateRoot,
  onDelete,
}: CategoriesTreeProps) {
  const fullTree = useMemo(() => buildCategoryTree(data), [data]);
  const { tree, expandIds: searchExpandIds } = useMemo(
    () => filterCategoryTree(fullTree, searchQuery),
    [fullTree, searchQuery],
  );

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (searchQuery.trim()) {
      setExpanded(new Set(searchExpandIds));
    }
  }, [searchQuery, searchExpandIds]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpanded(collectExpandableIds(tree));
  const collapseAll = () => setExpanded(new Set());

  if (isLoading) {
    return (
      <div className="space-y-2 px-3 py-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
            key={i}
            className="h-10 animate-pulse rounded-md bg-muted"
            style={{ marginLeft: `${(i % 3) * 1.25}rem` }}
          />
        ))}
      </div>
    );
  }

  if (tree.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center">
        <FolderIcon className="size-10 text-muted-foreground/50" />
        <div>
          <p className="font-medium text-sm">
            {searchQuery.trim() ? "ບໍ່ພົບຫມວດຫມູ່ທີ່ຄົ້ນຫາ" : "ຍັງບໍ່ມີຫມວດຫມູ່"}
          </p>
          <p className="mt-1 text-muted-foreground text-xs">
            {searchQuery.trim()
              ? "ລອງປ່ຽນຄຳຄົ້ນຫາ ຫຼື ລ້າງຕົວກອງ"
              : "ເພີ່ມຫມວດຫມູ່ຫຼັກ ແລ້ວສາມາດເພີ່ມຫມວດຍ່ອຍໄດ້"}
          </p>
        </div>
        {canCreate && !searchQuery.trim() ? (
          <Button type="button" size="sm" onClick={onCreateRoot}>
            <PlusIcon className="size-4" />
            ເພີ່ມຫມວດຫມູ່
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 px-2 pb-3">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <p className="text-muted-foreground text-xs">
          {data.length} ຫມວດທັງໝົດ · {tree.length} ຫມວດຫຼັກ
          {searchQuery.trim() ? " (ກອງແລ້ວ)" : ""}
        </p>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={expandAll}>
            ຂະຫຍາຍທັງໝົດ
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={collapseAll}>
            ຫຍໍ້ທັງໝົດ
          </Button>
        </div>
      </div>

      <ul className="m-0 list-none divide-y divide-border/60 rounded-lg border bg-background p-1">
        {tree.map((node) => (
          <CategoryTreeRow
            key={node.id}
            node={node}
            depth={0}
            expanded={expanded}
            onToggle={toggle}
            canManage={canManage}
            canCreate={canCreate}
            onEdit={onEdit}
            onAddChild={onAddChild}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </div>
  );
}
